import { FormattedMessage } from "react-intl";

import styles from "@components/carePlan/CarePlan.module.css";
import CareTeamInvite from "@components/carePlan/CareTeamInvite";
import CareTeamMember from "@components/carePlan/CareTeamMember";
import InviteAccountMemberForm from "@components/forms/InviteAccountMemberForm";
import IconBase from "@components/text/IconBase";
import BodyCopy from "@components/typography/BodyCopy";
import ButtonLink from "@components/typography/ButtonLink";
import Heading1 from "@components/typography/Heading1";
import useMyClient from "@hooks/useMyClient";
import useMyExpert from "@hooks/useMyExpert";
import useOpener from "@hooks/useOpener";
import { ClientRoleRoleEnum, GetJourneyWithPlanQuery } from "@typing/Generated";

type Journey = GetJourneyWithPlanQuery["journey"];

type Props = {
  journey: Journey;
};

const CareTeam = ({ journey }: Props) => {
  const myExpert = useMyExpert();
  const myClient = useMyClient();
  const inviteOpener = useOpener();

  const ownerRole = journey.clientRoles.find(role => role.role === ClientRoleRoleEnum.OWNER);

  const myRole = journey.clientRoles.find(role => role.client.id === myClient?.id);

  return (
    <div className={styles.mobileSpacing}>
      <div className={`ion-hide-lg-up ${styles.careTeamHeader}`}>
        <Heading1>
          <FormattedMessage id="pages.client.careProfile.careTeam.description" />
        </Heading1>
      </div>
      <div>
        {ownerRole && (
          <CareTeamMember
            canEdit={!!(myRole?.id === ownerRole.id || myExpert)}
            canRemove={false}
            color="secondary-500"
            journey={journey}
            role={ownerRole}
          />
        )}
        {journey.clientRoles
          .filter(clientRole => clientRole.id !== ownerRole?.id)
          .map((clientRole, index) => (
            <CareTeamMember
              canEdit={!!(myRole?.id === clientRole.id || myExpert)}
              canRemove={
                !!(myRole?.role === ClientRoleRoleEnum.OWNER || myExpert) &&
                clientRole.role !== ClientRoleRoleEnum.OWNER
              }
              color={
                index === 0 ? "primary-500" : index === 1 ? "primary" : index === 2 ? "secondary" : "secondary-500"
              }
              journey={journey}
              key={clientRole.id}
              role={clientRole}
            />
          ))}
        {journey.careTeamInvites
          ?.filter(invite => !invite.accepted && !invite.canceled)
          .map(careTeamInvite => <CareTeamInvite careTeamInvite={careTeamInvite} key={careTeamInvite.id} />)}
      </div>
      <div>
        {inviteOpener.isOpen ? (
          <div className={styles.inviteForm}>
            <div className="full-width">
              <InviteAccountMemberForm
                journey={journey}
                onCancel={() => inviteOpener.close()}
                onSuccess={inviteOpener.close}
              />
            </div>
          </div>
        ) : (
          <ButtonLink onClick={inviteOpener.toggle} stylized={false}>
            <div className="flex items-center">
              <div className={`${styles.addIconContainer} ion-margin-end`}>
                <IconBase className={styles.addIcon} color="gray-300" icon="plus" slot="start" />
              </div>
              <BodyCopy color="gray">
                <FormattedMessage id="pages.client.careProfile.careTeam.addSomeone" />
              </BodyCopy>
            </div>
          </ButtonLink>
        )}
      </div>
    </div>
  );
};

export default CareTeam;
